import { useMemo, useState } from "react"

import { CheckSquare, Search, Square, Workflow } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

import { useWorkflowStore } from "../../stores/workflow-store"
import { getAncestorNodes, getNodeLabel } from "../../utils/graph-utils"

// Control-flow node types do not produce referenceable content output.
const NON_REFERENCEABLE_NODE_TYPES = new Set([
  "start",
  "end",
  "loopContainer",
  "loopStart",
  "loopEnd",
  "parallel",
  "merge",
  "delay",
  "variable",
])

type ReferenceNodesPickerProps = {
  currentNodeId: string
  selectedNodeIds: string[]
  onChange: (nodeIds: string[]) => void
}

export function ReferenceNodesPicker({
  currentNodeId,
  selectedNodeIds,
  onChange,
}: ReferenceNodesPickerProps) {
  const nodes = useWorkflowStore((state) => state.nodes)
  const edges = useWorkflowStore((state) => state.edges)
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")

  // (A) Node references are NOT honored for nodes inside a loop container — the
  // loop engine chains child outputs sequentially and never resolves
  // nodeReferences. The editor also blocks edges across the loop boundary, so a
  // loop child can't even reach its pre-loop ancestors. So don't offer the picker
  // there rather than silently dropping the selection at runtime.
  const isInLoop = useMemo(() => {
    const self = nodes.find((node) => node.id === currentNodeId)
    if (!self?.parentId) return false
    return nodes.some((node) => node.id === self.parentId && node.type === "loopContainer")
  }, [nodes, currentNodeId])

  const availableNodes = useMemo(() => {
    return getAncestorNodes(currentNodeId, nodes, edges).filter(
      (node) => !NON_REFERENCEABLE_NODE_TYPES.has(node.type ?? "")
    )
  }, [currentNodeId, nodes, edges])

  // (B) Selected ids that are no longer available upstream (the referenced node
  // was deleted or moved off the ancestor path). They'd otherwise be invisible —
  // the count shows but there's no row to untick — so surface them for removal.
  const staleSelected = useMemo(() => {
    const availableIds = new Set(availableNodes.map((node) => node.id))
    return selectedNodeIds.filter((id) => !availableIds.has(id))
  }, [availableNodes, selectedNodeIds])

  const filteredNodes = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) return availableNodes
    return availableNodes.filter(
      (node) =>
        getNodeLabel(node).toLowerCase().includes(query) || node.id.toLowerCase().includes(query)
    )
  }, [availableNodes, searchQuery])

  const toggleNode = (nodeId: string) => {
    if (selectedNodeIds.includes(nodeId)) {
      onChange(selectedNodeIds.filter((id) => id !== nodeId))
    } else {
      onChange([...selectedNodeIds, nodeId])
    }
  }

  const labelForId = (nodeId: string) => {
    const node = nodes.find((n) => n.id === nodeId)
    return node ? getNodeLabel(node) : nodeId
  }

  const renderRow = (id: string, label: string, subtitle: string) => {
    const isActive = selectedNodeIds.includes(id)
    return (
      <button
        key={id}
        type="button"
        className={cn(
          "flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left transition-colors",
          isActive ? "bg-primary/10 ring-primary/30 ring-1" : "hover:bg-muted/50"
        )}
        onClick={() => toggleNode(id)}
      >
        <span
          className={cn("mt-0.5 shrink-0", isActive ? "text-primary" : "text-muted-foreground")}
        >
          {isActive ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium">{label}</div>
          <div className="text-muted-foreground line-clamp-1 text-xs">{subtitle}</div>
        </div>
      </button>
    )
  }

  // (A) Inside a loop: the engine won't resolve references here, so show a disabled
  // affordance. But if the node carries orphaned selections (e.g. it was moved INTO
  // the loop after references were set), surface them with a one-click clear so they
  // don't persist invisibly (the normal staleSelected cleanup UI isn't reachable here).
  if (isInLoop) {
    if (selectedNodeIds.length > 0) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              aria-label={`Clear ${selectedNodeIds.length} inactive node reference(s)`}
              className="nodrag text-warning-ink -m-0.5 size-6 p-0"
              size="icon"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation()
                onChange([])
              }}
            >
              <Workflow className="h-3 w-3" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">
            <p className="text-xs">
              {selectedNodeIds.length} node reference{selectedNodeIds.length > 1 ? "s" : ""}{" "}
              won&rsquo;t resolve inside loops — click to clear
            </p>
          </TooltipContent>
        </Tooltip>
      )
    }
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="nodrag -m-0.5 flex size-6 cursor-not-allowed items-center justify-center opacity-40">
            <Workflow className="h-3 w-3" />
          </span>
        </TooltipTrigger>
        <TooltipContent side="top">
          <p className="text-xs">Node references aren&rsquo;t available inside loops</p>
        </TooltipContent>
      </Tooltip>
    )
  }

  // The field label is rendered here (not the 8 node editors) and is omitted inside
  // loops, where the picker collapses to a small disabled icon and a full-size label
  // above it reads oddly.
  return (
    <div className="grid gap-1.5">
      <Label className="text-xs">Reference nodes</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            className="nodrag -m-0.5 size-6 p-0"
            size="icon"
            variant="ghost"
            aria-label={
              selectedNodeIds.length > 0
                ? `Reference nodes (${selectedNodeIds.length} selected)`
                : "Reference nodes"
            }
            onClick={(e) => e.stopPropagation()}
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex items-center justify-center">
                  <Workflow className="h-3 w-3" />
                </span>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p className="text-xs">
                  Reference nodes
                  {selectedNodeIds.length > 0 && (
                    <span className="ml-1 opacity-70">({selectedNodeIds.length})</span>
                  )}
                </p>
              </TooltipContent>
            </Tooltip>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="nodrag nowheel w-[320px] p-0"
          side="right"
          onClick={(e) => e.stopPropagation()}
        >
          {availableNodes.length === 0 && staleSelected.length === 0 ? (
            <div className="text-muted-foreground p-4 text-center text-sm">
              No upstream nodes to reference
            </div>
          ) : (
            <>
              {availableNodes.length > 0 && (
                <div className="p-2">
                  <div className="relative">
                    <Search className="text-muted-foreground absolute top-2 left-2 h-4 w-4" />
                    <Input
                      className="h-8 pl-8 text-sm"
                      placeholder="Search nodes..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                </div>
              )}
              <div className="max-h-[280px] overflow-y-auto p-2 pt-0">
                {availableNodes.length > 0 && filteredNodes.length === 0 && (
                  <div className="text-muted-foreground py-4 text-center text-sm">No results</div>
                )}
                {filteredNodes.length > 0 && (
                  <div className="space-y-1">
                    {filteredNodes.map((node) => renderRow(node.id, getNodeLabel(node), node.id))}
                  </div>
                )}
                {staleSelected.length > 0 && (
                  <>
                    <div className="text-muted-foreground px-2 pt-3 pb-1 text-[10px] font-medium tracking-wide uppercase">
                      Selected · no longer upstream
                    </div>
                    <div className="space-y-1">
                      {staleSelected.map((id) =>
                        renderRow(id, labelForId(id), "No longer available — click to remove")
                      )}
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </PopoverContent>
      </Popover>
    </div>
  )
}
